import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  MapPin,
  Pencil,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { AreaFormDialog } from "@/components/admin/AreaFormDialog";
import { formatDateShort } from "@/lib/format/date";

interface Area {
  id: string;
  name: string;
  pincodes: string[];
  pickupFee: number;
  deliveryFee: number;
  returnFee: number;
  slaCutoffTime: string;
  serviceable: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    slots: number;
    addresses: number;
  };
}

export default function AdminAreasPage() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);

  const fetchAreas = async () => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/admin/areas");
      const data = await res.json();
      if (res.ok) {
        setAreas(data.areas);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const handleToggleActive = async (area: Area) => {
    try {
      const res = await fetch(`/api/admin/areas/${area.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !area.active }),
      });
      if (res.ok) {
        fetchAreas();
      }
    } catch (error) {
      console.error("Error toggling area:", error);
    }
  };

  const handleToggleServiceable = async (area: Area) => {
    try {
      const res = await fetch(`/api/admin/areas/${area.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serviceable: !area.serviceable }),
      });
      if (res.ok) {
        fetchAreas();
      }
    } catch (error) {
      console.error("Error toggling area:", error);
    }
  };

  const filteredAreas = areas.filter(
    (area) =>
      area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      area.pincodes.some((p) => p.includes(searchQuery))
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Coverage Areas"
        subtitle="Manage serviceable areas, pincodes, and fees"
        actions={
          <Button
            onClick={() => {
              setEditingArea(null);
              setIsFormOpen(true);
            }}
            className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Area
          </Button>
        }
      />

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or pincode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchAreas} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pincodes</TableHead>
                <TableHead>Fees</TableHead>
                <TableHead>Cutoff</TableHead>
                <TableHead>Stats</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-8" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : filteredAreas.length === 0 ? (
        searchQuery ? (
          <div className="rounded-lg border border-slate-200 bg-white p-12">
            <EmptyState
              icon={Search}
              title="No areas found"
              description={`No areas match "${searchQuery}". Try a different search.`}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-12">
            <EmptyState
              icon={MapPin}
              title="No coverage areas yet"
              description="Create your first coverage area to start managing serviceable locations."
              action={
                <Button
                  onClick={() => {
                    setEditingArea(null);
                    setIsFormOpen(true);
                  }}
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Area
                </Button>
              }
            />
          </div>
        )
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Pincodes</TableHead>
                <TableHead className="font-semibold">Fees</TableHead>
                <TableHead className="font-semibold">Cutoff</TableHead>
                <TableHead className="font-semibold">Stats</TableHead>
                <TableHead className="font-semibold">Updated</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAreas.map((area) => (
                <TableRow key={area.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="font-medium text-slate-900">{area.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {area.active ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 w-fit">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 w-fit">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                      {area.serviceable ? (
                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 w-fit">
                          Serviceable
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 w-fit">
                          Not Serviceable
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                      {area.pincodes.slice(0, 3).map((p) => (
                        <Badge key={p} variant="secondary" className="text-xs">
                          {p}
                        </Badge>
                      ))}
                      {area.pincodes.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{area.pincodes.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm space-y-0.5">
                      <div className="text-slate-600">
                        P: {formatCurrency(area.pickupFee)}
                      </div>
                      <div className="text-slate-600">
                        D: {formatCurrency(area.deliveryFee)}
                      </div>
                      <div className="text-slate-600">
                        R: {formatCurrency(area.returnFee)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{area.slaCutoffTime}</span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm space-y-0.5">
                      <div className="text-slate-600">
                        {area._count.slots} slots
                      </div>
                      <div className="text-slate-600">
                        {area._count.addresses} addresses
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {formatDateShort(area.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingArea(area);
                            setIsFormOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleServiceable(area)}
                        >
                          {area.serviceable ? (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              Mark Not Serviceable
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Serviceable
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleActive(area)}
                        >
                          {area.active ? (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AreaFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        area={editingArea}
        onSuccess={fetchAreas}
      />
    </div>
  );
}
